export interface MembershipGridOptions {
  universe: readonly string[];
  columns: readonly { id: string; label: string }[];
  isMember(elementIndex: number, columnId: string): boolean;
  highlightColumn?: string | null;
}

export interface MembershipGridHandle {
  element: HTMLElement;
  update(opts: Partial<MembershipGridOptions>): void;
  destroy(): void;
}

/**
 * Builds a `<table>` showing which universe elements belong to each column set.
 *
 * - Header row: one `<th>` per column (pre-translated label).
 * - Body: one `<tr>` per universe element; each membership `<td>` carries
 *   `data-member="true|false"` and renders ✓ when true.
 * - The `highlightColumn` column's cells get the `is-highlight` CSS class.
 * - All colours come from CSS custom properties — no inline colour attributes.
 */
export function createMembershipGrid(
  parent: HTMLElement,
  opts: MembershipGridOptions,
): MembershipGridHandle {
  let current = { ...opts };

  // Build the table skeleton.
  const table = document.createElement('table');
  table.className = 'membership-grid';

  // <thead>
  const thead = document.createElement('thead');
  table.appendChild(thead);

  // Render the header row from scratch.
  function renderHeader(): void {
    thead.innerHTML = '';
    const headerRow = document.createElement('tr');
    // Empty leading <th> for the element-label column.
    const cornerTh = document.createElement('th');
    headerRow.appendChild(cornerTh);
    for (const col of current.columns) {
      const th = document.createElement('th');
      th.dataset['colId'] = col.id;
      th.textContent = col.label;
      headerRow.appendChild(th);
    }
    thead.appendChild(headerRow);
  }

  renderHeader();

  // <tbody>
  const tbody = document.createElement('tbody');
  table.appendChild(tbody);

  parent.appendChild(table);

  // Render all body rows from scratch.
  function renderRows(): void {
    tbody.innerHTML = '';
    for (let i = 0; i < current.universe.length; i++) {
      const tr = document.createElement('tr');

      // Element-label cell.
      const labelTd = document.createElement('td');
      labelTd.className = 'membership-grid__element';
      labelTd.textContent = current.universe[i] ?? '';
      tr.appendChild(labelTd);

      // One membership cell per column.
      for (const col of current.columns) {
        const member = current.isMember(i, col.id);
        const td = document.createElement('td');
        td.dataset['member'] = member ? 'true' : 'false';
        td.className = 'membership-grid__cell';
        if (current.highlightColumn === col.id) {
          td.classList.add('is-highlight');
        }
        td.textContent = member ? '✓' : '';
        tr.appendChild(td);
      }

      tbody.appendChild(tr);
    }
  }

  renderRows();

  return {
    element: table,

    update(partial: Partial<MembershipGridOptions>): void {
      current = { ...current, ...partial };
      renderHeader();
      renderRows();
    },

    destroy(): void {
      if (table.parentNode) {
        table.parentNode.removeChild(table);
      }
    },
  };
}
